import { cn } from '@cyphex/ui';
import { lastSeenClause } from '../lastSeen';
import { ACTIVITY_META } from '../presenceMeta';
import type { ActivityState } from '../types';

export interface PresenceLineProps {
  state: ActivityState;
  /** Epoch ms of the last real activity. `null` when we have never been told. */
  lastActivityAt: number | null;
  /**
   * Rendered in place of the state word — a custom status ("🎧 Focus time"), a
   * job title, whatever the surface already put there. The last-seen clause is
   * unaffected: it is a fact about the PERSON, not about the label, and "are
   * they about to reply" survives them having set a status.
   */
  label?: string | null;
  /**
   * `p` (default) where this is a line of its own; `span` where the surface has
   * already opened a paragraph and wants only the text.
   */
  as?: 'p' | 'span';
  className?: string;
}

/**
 * "Away · Last seen 15 minutes ago".
 *
 * Microsoft lists `Away, Last Seen <time>` as a presence state in its own
 * right, and it is the only one that answers the question people actually have
 * when they see an amber dot: is this person about to reply, or did they shut
 * the laptop on Friday? Away with no age is the same badge whether they stepped
 * out for coffee ninety seconds ago or a week ago.
 *
 * THIS IS THE ONLY IMPLEMENTATION, and every surface that names a person's
 * state goes through it — the profile card and its hover popover, the profile
 * availability block, the thread header, the people-online roster. Writing the
 * sentence again per surface is exactly how the thread header ended up saying a
 * bare "Away" while the card next to it said more.
 *
 * WHEN it shows is `lastSeenClause`'s decision, so the surfaces that can only
 * afford a dot (chat list rows, message avatars, team member lists) put the same
 * sentence in the dot's accessible name without a second copy of the rule.
 */
export function PresenceLine({
  state,
  lastActivityAt,
  label,
  as = 'p',
  className,
}: PresenceLineProps) {
  const text = label ?? ACTIVITY_META[state].label;
  const clause = lastSeenClause(state, lastActivityAt);
  const Tag = as;

  return (
    <Tag className={cn(as === 'p' && 'mt-1', 'text-md text-muted-foreground', className)}>
      {text}
      {clause === null ? null : (
        <>
          <span aria-hidden> · </span>
          <span>{clause}</span>
        </>
      )}
    </Tag>
  );
}
